import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import {
  Users,
  Search,
  Eye,
  FileText,
  Clock,
  Award,
  CalendarOff,
  Building2,
  Loader2,
  Mail,
  Phone,
  DollarSign,
  Calendar
} from "lucide-react";
import { toast } from "sonner";
import { format, formatDistanceToNow } from "date-fns";
import { fmtTz } from "../lib/time";

export default function AdminEmployeeRecords() {
  const { authAxios, user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeRecord, setEmployeeRecord] = useState(null);
  const [loadingRecord, setLoadingRecord] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const isAdmin = user?.role === 'admin';

  const fetchEmployees = useCallback(async () => {
    try {
      const res = await authAxios.get('/employee-records');
      setEmployees(res.data);
    } catch (error) {
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    if (isAdmin) {
      fetchEmployees();
    } else {
      // Employee viewing their own record
      fetchMyRecord();
    }
  }, [fetchEmployees, isAdmin]);

  const fetchMyRecord = async () => {
    try {
      const res = await authAxios.get('/employee-records/me');
      setEmployeeRecord(res.data);
    } catch (error) {
      toast.error('Failed to load your record');
    } finally {
      setLoading(false);
    }
  };

  const viewEmployeeRecord = async (employee) => {
    setSelectedEmployee(employee);
    setLoadingRecord(true);
    setDialogOpen(true);
    
    try {
      const res = await authAxios.get(`/employee-records/${employee.id}`);
      setEmployeeRecord(res.data);
    } catch (error) {
      toast.error('Failed to load employee record');
    } finally {
      setLoadingRecord(false);
    }
  };

  const filteredEmployees = employees.filter(e => 
    e.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    e.email?.toLowerCase().includes(search.toLowerCase()) ||
    e.department?.toLowerCase().includes(search.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      pending_approval: 'bg-amber-100 text-amber-700 border-amber-200',
      inactive: 'bg-stone-100 text-stone-600 border-stone-200'
    };
    return <Badge variant="outline" className={styles[status] || styles.active}>{status?.replace('_', ' ')}</Badge>;
  };

  const getCertStatusBadge = (status) => {
    const styles = {
      valid: 'bg-emerald-100 text-emerald-700',
      expiring_soon: 'bg-amber-100 text-amber-700',
      expired: 'bg-rose-100 text-rose-700'
    };
    return <Badge className={styles[status]}>{status?.replace('_', ' ')}</Badge>;
  };

  const EmployeeRecordView = ({ record }) => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4 p-4 bg-stone-50 rounded-lg">
        <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
          {record.full_name?.split(' ').map(n => n[0]).join('')}
        </div>
        <div className="flex-1">
          <h3 className="text-xl font-bold">{record.full_name}</h3>
          <p className="text-stone-600">{record.job_title || 'Employee'} • {record.department || 'General'}</p>
          <div className="flex items-center gap-4 mt-2 text-sm text-stone-500">
            <span className="flex items-center gap-1"><Mail className="w-4 h-4" />{record.email}</span>
            {record.phone && <span className="flex items-center gap-1"><Phone className="w-4 h-4" />{record.phone}</span>}
          </div>
        </div>
        <div className="text-right">
          {getStatusBadge(record.status)}
          {record.employee_id && <p className="text-sm text-stone-500 mt-1">ID: {record.employee_id}</p>}
        </div>
      </div>

      <Tabs defaultValue="summary" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="summary">Summary</TabsTrigger>
          <TabsTrigger value="time">Time Entries</TabsTrigger>
          <TabsTrigger value="projects">Projects</TabsTrigger>
          <TabsTrigger value="certifications">Certifications</TabsTrigger>
          <TabsTrigger value="timeoff">Time Off</TabsTrigger>
        </TabsList>

        <TabsContent value="summary" className="space-y-4 mt-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <Clock className="w-8 h-8 mx-auto text-emerald-600 mb-2" />
                <p className="text-2xl font-bold">{record.time_entries_summary?.total_hours || 0}</p>
                <p className="text-sm text-stone-500">Total Hours</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <DollarSign className="w-8 h-8 mx-auto text-emerald-600 mb-2" />
                <p className="text-2xl font-bold">${record.time_entries_summary?.total_earnings?.toLocaleString() || 0}</p>
                <p className="text-sm text-stone-500">Total Earnings</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <Building2 className="w-8 h-8 mx-auto text-emerald-600 mb-2" />
                <p className="text-2xl font-bold">{record.projects_worked?.length || 0}</p>
                <p className="text-sm text-stone-500">Projects</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <Award className="w-8 h-8 mx-auto text-emerald-600 mb-2" />
                <p className="text-2xl font-bold">{record.certifications?.length || 0}</p>
                <p className="text-sm text-stone-500">Certifications</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Employment Details</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <dt className="text-stone-500">Hire Date</dt>
                  <dd className="font-medium">{record.hire_date ? fmtTz(record.hire_date, 'MMM d, yyyy') : '-'}</dd>
                </div>
                <div>
                  <dt className="text-stone-500">Hourly Rate</dt>
                  <dd className="font-medium">${record.hourly_rate?.toFixed(2) || '0.00'}/hr</dd>
                </div>
                <div>
                  <dt className="text-stone-500">Role</dt>
                  <dd className="font-medium capitalize">{record.role}</dd>
                </div>
                <div>
                  <dt className="text-stone-500">Department</dt>
                  <dd className="font-medium">{record.department || '-'}</dd>
                </div>
                {record.emergency_contact_name && (
                  <>
                    <div>
                      <dt className="text-stone-500">Emergency Contact</dt>
                      <dd className="font-medium">{record.emergency_contact_name}</dd>
                    </div>
                    <div>
                      <dt className="text-stone-500">Emergency Phone</dt>
                      <dd className="font-medium">{record.emergency_contact_phone}</dd>
                    </div>
                  </>
                )}
              </dl>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="time" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Recent Time Entries</CardTitle>
              <CardDescription>{record.time_entries_summary?.total_entries || 0} total entries</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-stone-500">Time entry details available in Records section</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="projects" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Projects Worked</CardTitle>
            </CardHeader>
            <CardContent>
              {record.projects_worked?.length === 0 ? (
                <p className="text-sm text-stone-500">No projects yet</p>
              ) : (
                <div className="space-y-3">
                  {record.projects_worked?.map((project, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                      <div>
                        <p className="font-medium">{project.project_name}</p>
                        <p className="text-sm text-stone-500">{project.entry_count} entries</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-emerald-600">{project.total_hours} hrs</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="certifications" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Certifications</CardTitle>
            </CardHeader>
            <CardContent>
              {record.certifications?.length === 0 ? (
                <p className="text-sm text-stone-500">No certifications on file</p>
              ) : (
                <div className="space-y-3">
                  {record.certifications?.map((cert, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                      <div>
                        <p className="font-medium">{cert.name}</p>
                        <p className="text-sm text-stone-500">Expires: {fmtTz(cert.expiry_date, 'MMM d, yyyy')}</p>
                      </div>
                      <div className="text-right">
                        {getCertStatusBadge(cert.status)}
                        <p className="text-xs text-stone-500 mt-1">{cert.days_until_expiry} days</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="timeoff" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Time Off History</CardTitle>
            </CardHeader>
            <CardContent>
              {record.time_off_history?.length === 0 ? (
                <p className="text-sm text-stone-500">No time off requests</p>
              ) : (
                <div className="space-y-3">
                  {record.time_off_history?.map((req, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                      <div>
                        <p className="font-medium capitalize">{req.request_type}</p>
                        <p className="text-sm text-stone-500">
                          {fmtTz(req.start_date, 'MMM d')} - {fmtTz(req.end_date, 'MMM d, yyyy')}
                        </p>
                      </div>
                      <Badge variant="outline" className={
                        req.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                        req.status === 'denied' ? 'bg-rose-100 text-rose-700' :
                        'bg-amber-100 text-amber-700'
                      }>
                        {req.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );

  // Employee view - show their own record
  if (!isAdmin) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="my-employee-record">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-stone-900 font-heading">My Employment Record</h1>
          <p className="text-stone-500 mt-1">View your complete employment history and details</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
          </div>
        ) : employeeRecord ? (
          <Card className="border-0 shadow-lg">
            <CardContent className="p-6">
              <EmployeeRecordView record={employeeRecord} />
            </CardContent>
          </Card>
        ) : (
          <Card className="border-0 shadow-lg">
            <CardContent className="py-12 text-center">
              <p className="text-stone-500">Unable to load your record</p>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // Admin view - list all employees
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-employee-records">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-stone-900 font-heading">Employee Records</h1>
        <p className="text-stone-500 mt-1">View comprehensive employee profiles and history</p>
      </div>

      {/* Search */}
      <Card className="border-0 shadow-lg mb-6">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <Input
              placeholder="Search employees by name, email, or department..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="employee-search"
            />
          </div>
        </CardContent>
      </Card>

      {/* Employee List */}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <Users className="w-5 h-5 text-stone-400" />
            Employees ({filteredEmployees.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
            </div>
          ) : filteredEmployees.length === 0 ? (
            <div className="text-center py-12 text-stone-500">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No employees found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-stone-100">
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Employee</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Department</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Role</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Hourly Rate</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Status</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEmployees.map((emp) => (
                    <TableRow key={emp.id} className="border-stone-100 hover:bg-stone-50">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-700 font-semibold text-sm">
                            {emp.full_name?.split(' ').map(n => n[0]).join('')}
                          </div>
                          <div>
                            <p className="font-medium">{emp.full_name}</p>
                            <p className="text-sm text-stone-500">{emp.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-stone-600">{emp.department || '-'}</TableCell>
                      <TableCell className="capitalize">{emp.role}</TableCell>
                      <TableCell className="font-medium">${emp.hourly_rate?.toFixed(2) || '0.00'}/hr</TableCell>
                      <TableCell>{getStatusBadge(emp.status)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => viewEmployeeRecord(emp)}
                          data-testid={`view-record-${emp.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View Record
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Employee Record Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-emerald-600" />
              Employee Record
            </DialogTitle>
          </DialogHeader>
          {loadingRecord ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
            </div>
          ) : employeeRecord ? (
            <EmployeeRecordView record={employeeRecord} />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
